'use client'
import Image from 'next/image'

import { stegaClean } from 'next-sanity'

import { Icon } from '@/app/components/Icon'
import { Link } from '@/app/components/Link'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type {
  Icon as IconType,
  Link as LinkType,
  Publisher,
  PublisherCompany,
  Publishers as PublishersPropsTypegen,
} from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

type PublisherItem =
  | (Pick<Publisher, 'publisherName' | 'subscribersCount' | '_id'> & {
      icon?: IconType
      link?: LinkType | null
      type: 'publisher'
    })
  | (Pick<PublisherCompany, 'companyName' | 'subscribersCount' | '_id' | 'logo'> & {
      link?: LinkType | null
      type: 'publisherCompany'
    })

interface PublishersProps {
  value: PublishersPropsTypegen
}

type RawPublisher = Pick<Publisher, 'publisherName' | 'subscribersCount' | '_id'> & {
  icon?: IconType
  link?: LinkType | null
}

type RawPublisherCompany = Pick<
  PublisherCompany,
  'companyName' | 'subscribersCount' | '_id' | 'logo'
> & {
  link?: LinkType | null
}

function isPublisher(
  item: RawPublisher & { type: 'publisher' },
): item is PublisherItem & { type: 'publisher' } {
  return item !== null && item.type === 'publisher'
}

function isPublisherCompany(
  item: RawPublisherCompany & { type: 'publisherCompany' },
): item is PublisherItem & { type: 'publisherCompany' } {
  return item !== null && item.type === 'publisherCompany'
}

export function Publishers({ value }: PublishersProps) {
  const publisherType = value.publisherType || 'publishers'
  const showBorders = value.showBorders ?? true

  const items: PublisherItem[] = []

  if (publisherType === 'publishers' && value.publishers) {
    const typedPublishers: Array<RawPublisher & { type: 'publisher' }> = (
      value.publishers as unknown as RawPublisher[]
    )
      .filter((p): p is RawPublisher => p !== null)
      .map((p) => ({ ...p, type: 'publisher' as const }))

    typedPublishers.forEach((item) => {
      if (isPublisher(item)) {
        items.push(item)
      }
    })
  } else if (publisherType === 'publisherCompanies' && value.publisherCompanies) {
    const typedCompanies: Array<RawPublisherCompany & { type: 'publisherCompany' }> = (
      value.publisherCompanies as unknown as RawPublisherCompany[]
    )
      .filter((p): p is RawPublisherCompany => p !== null)
      .map((p) => ({ ...p, type: 'publisherCompany' as const }))

    typedCompanies.forEach((item) => {
      if (isPublisherCompany(item)) {
        items.push(item)
      }
    })
  }

  const borderColors = [
    'border-neon-300',
    'border-coral-400',
    'border-green-300',
    'border-purple-400',
  ]

  // Titles longer than this get a wider tile to avoid text wrapping/overflow
  const WIDE_TILE_NAME_LENGTH = 16
  const DEFAULT_TILE_WIDTH = 280
  const WIDE_TILE_WIDTH = 310

  const isWideTile = (item: PublisherItem) => {
    const displayName = item.type === 'publisherCompany' ? item.companyName : item.publisherName
    return (stegaClean(displayName) ?? '').length > WIDE_TILE_NAME_LENGTH
  }

  if (items.length === 0) {
    return null
  }

  const renderPublisherItem = (item: PublisherItem, index: number, setNumber: number) => {
    const borderColorClass = borderColors[index % borderColors.length]
    const displayName = item.type === 'publisherCompany' ? item.companyName : item.publisherName

    const logoUrl =
      item.type === 'publisherCompany' && item.logo ? urlForImage(item.logo)?.url() : null

    const tileContent = (
      <div
        className={cn(
          'flex flex-row items-center justify-between gap-md rounded-md p-lg flex-shrink-0 bg-neutral-50',
          isWideTile(item) ? 'w-[310px]' : 'w-[280px]',
          showBorders && `border-3 ${borderColorClass}`,
        )}
      >
        <div className="w-[60px] h-[60px] flex shrink-0 items-center justify-center rounded-sm bg-white-default text-white-900">
          {item.type === 'publisher' && item.icon && item.icon.provider && item.icon.name ? (
            <Icon
              icon={{
                _type: 'icon',
                provider: item.icon.provider,
                style: item.icon.style || null,
                name: item.icon.name,
              }}
              size={22}
              className="w-full h-full"
            />
          ) : item.type === 'publisherCompany' && logoUrl ? (
            <Image
              src={logoUrl}
              alt={displayName || ''}
              width={60}
              height={60}
              className="object-contain"
            />
          ) : null}
        </div>
        <div className="flex flex-col gap-0 items-start w-full">
          <p className="size-header-h5 !text-whte-950">{displayName}</p>
          {item.subscribersCount && (
            <p className="size-body-xs !text-neutral-800">{item.subscribersCount} subscribers</p>
          )}
        </div>
      </div>
    )

    return (
      <div key={`${item._id}-${setNumber}-${index}`}>
        {item.link ? (
          <Link
            link={item.link as any}
            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT, section: 'publishers' }}
            className="block"
          >
            {tileContent}
          </Link>
        ) : (
          tileContent
        )}
      </div>
    )
  }

  const gapWidth = 24
  const totalItems = items.length
  const setWidth = items.reduce(
    (sum, item) => sum + (isWideTile(item) ? WIDE_TILE_WIDTH : DEFAULT_TILE_WIDTH),
    0,
  )
  const translateDistance = setWidth + gapWidth * (totalItems - 1)

  return (
    <>
      <style jsx>{`
        @keyframes publishers-marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${translateDistance}px);
          }
        }
        .publishers-marquee-container {
          animation: publishers-marquee 30s linear infinite;
        }
        .publishers-marquee-container:hover {
          animation-play-state: paused;
        }
      `}</style>
      <div className="my-9xl overflow-x-hidden w-[100dvw] relative left-[50%] right-[50%] ml-[-50vw] mr-[-50vw]">
        <div className={cn('flex flex-row gap-2xl publishers-marquee-container')}>
          {items.map((item, index) => renderPublisherItem(item, index, 0))}
          {items.map((item, index) => renderPublisherItem(item, index, 1))}
          {items.map((item, index) => renderPublisherItem(item, index, 2))}
        </div>
      </div>
    </>
  )
}
